import React, { useState, useEffect, useRef } from 'react';

const signs = [
  {
    title: 'Snoring',
    description:
      'Loud, chronic snoring is caused by vibrating throat tissues as air struggles through a narrowed airway. It is one of the most common and recognisable warning signs of obstructive sleep apnea in adults.',
    fact: 'Over 50% of heavy snorers have sleep apnea',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <ellipse cx="34" cy="44" rx="12" ry="14" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M28 34 Q34 28 40 34" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M46 36 Q50 30 54 28" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M48 30 Q52 24 56 22" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M50 24 Q54 18 58 17" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round"/>
        <text x="46" y="31" fill="#c05675" fontSize="9" fontWeight="500">z</text>
        <text x="50" y="24" fill="#c05675" fontSize="7" fontWeight="500">z</text>
        <text x="53" y="18" fill="#c05675" fontSize="5" fontWeight="500">z</text>
      </svg>
    ),
  },
  {
    title: 'Irritability',
    description:
      'Fragmented sleep caused by repeated breathing interruptions prevents the brain from completing restorative sleep cycles. This leads to mood dysregulation, short temper, and emotional instability during the day.',
    fact: 'Sleep deprivation impairs emotional control centres',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <circle cx="40" cy="42" r="16" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="34" cy="39" r="2" fill="#c05675"/>
        <circle cx="46" cy="39" r="2" fill="#c05675"/>
        <path d="M33 48 Q40 44 47 48" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M31 33 L28 29 M33 32 L31 28" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M49 33 L52 29 M47 32 L49 28" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: 'Choking in Sleep',
    description:
      'When the airway collapses completely during sleep, the brain triggers a panic response causing the person to gasp, choke, or briefly wake. These episodes can occur dozens of times per night without the person remembering them.',
    fact: 'Gasping episodes signal complete airway collapse',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <circle cx="40" cy="40" r="15" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="34" cy="37" r="2.5" fill="#c05675"/>
        <circle cx="46" cy="37" r="2.5" fill="#c05675"/>
        <path d="M34 46 Q40 43 46 46" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M40 25 L40 20 M36 22 L40 20 L44 22" stroke="#c05675" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Morning Headaches',
    description:
      'Low oxygen levels and high carbon dioxide during repeated apnea events cause blood vessels in the brain to dilate. This vascular response is felt as a dull, throbbing headache upon waking that typically fades within an hour.',
    fact: 'Headaches upon waking are a classic OSA sign',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <circle cx="40" cy="44" r="14" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M33 37 Q40 33 47 37" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        <path d="M40 30 L40 24" stroke="#c05675" strokeWidth="2" strokeLinecap="round"/>
        <path d="M34 31 L30 27" stroke="#c05675" strokeWidth="2" strokeLinecap="round"/>
        <path d="M46 31 L50 27" stroke="#c05675" strokeWidth="2" strokeLinecap="round"/>
        <path d="M36 47 Q40 44 44 47" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <circle cx="36" cy="43" r="1.5" fill="#c05675"/>
        <circle cx="44" cy="43" r="1.5" fill="#c05675"/>
      </svg>
    ),
  },
  {
    title: 'Insomnia',
    description:
      'Sleep apnea and insomnia frequently co-occur. The repeated arousals from breathing events train the brain to associate bed with wakefulness. Many adults with sleep apnea struggle to fall or stay asleep even when exhausted.',
    fact: 'Up to 50% of insomnia patients have sleep apnea',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <rect x="24" y="44" width="32" height="12" rx="3" stroke="#c05675" strokeWidth="2" fill="none"/>
        <rect x="28" y="36" width="10" height="10" rx="2" stroke="#c05675" strokeWidth="1.8" fill="none"/>
        <circle cx="53" cy="28" r="8" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M53 24 L53 28 L56 30" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M49 20 L47 18 M57 20 L59 18 M61 28 L63 28" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: 'Poor Libido',
    description:
      'Chronic sleep deprivation from sleep apnea suppresses testosterone and other hormones critical for sexual health. Studies show significant improvement in libido and sexual function after successful sleep apnea treatment.',
    fact: 'Testosterone drops significantly with untreated OSA',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <circle cx="40" cy="36" r="12" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="35" cy="34" r="1.5" fill="#c05675"/>
        <circle cx="45" cy="34" r="1.5" fill="#c05675"/>
        <path d="M36 41 Q40 38 44 41" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <path d="M32 48 Q40 52 48 48" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round" fill="none" strokeDasharray="2 2"/>
        <path d="M53 24 L56 21 M55 24 L56 21 L53 21" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: 'Poor Workplace Performance',
    description:
      'Cognitive impairment from sleep deprivation reduces concentration, memory, and decision-making ability. Adults with untreated sleep apnea report significantly lower work productivity, more errors, and difficulty completing complex tasks.',
    fact: 'OSA costs employers billions in lost productivity',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <rect x="26" y="38" width="28" height="18" rx="2" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M32 38 L32 34 Q32 28 40 28 Q48 28 48 34 L48 38" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <circle cx="40" cy="33" r="5" stroke="#c05675" strokeWidth="1.5" fill="none"/>
        <path d="M30 46 L34 46 M36 46 L50 46" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round"/>
        <text x="35" y="52" fill="#c05675" fontSize="7" fontWeight="500">z z</text>
      </svg>
    ),
  },
  {
    title: 'Excessive Daytime Sleepiness',
    description:
      'Without reaching deep, restorative sleep, the body never fully recharges. This causes overwhelming daytime drowsiness that interferes with work, driving, and social activities — and is distinct from simply feeling tired.',
    fact: 'Daytime sleepiness is the #1 OSA complaint',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <circle cx="40" cy="40" r="14" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M33 38 Q34 40 36 38" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M44 38 Q45 40 47 38" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M35 46 Q40 50 45 46" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M30 32 Q32 29 35 31" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        <path d="M45 31 Q48 29 50 32" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        <circle cx="54" cy="28" r="4" stroke="#c05675" strokeWidth="1.5" fill="none"/>
        <path d="M57 24 L59 22 M54 24 L54 22 M51 24 L49 22" stroke="#c05675" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: 'Mood Changes',
    description:
      'Disrupted sleep architecture caused by sleep apnea destabilises mood regulation. Patients commonly experience increased anxiety, depression, emotional blunting, and difficulty managing stress — symptoms that often resolve with treatment.',
    fact: '5× increased risk of clinical depression',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <circle cx="31" cy="40" r="11" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="27" cy="38" r="1.5" fill="#c05675"/>
        <circle cx="35" cy="38" r="1.5" fill="#c05675"/>
        <path d="M27 44 Q31 42 35 44" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <circle cx="52" cy="40" r="11" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="48" cy="38" r="1.5" fill="#c05675"/>
        <circle cx="56" cy="38" r="1.5" fill="#c05675"/>
        <path d="M48 46 Q52 43 56 46" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <path d="M40 34 L40 46" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2"/>
      </svg>
    ),
  },
  {
    title: 'Drowsy Driving',
    description:
      'Adults with untreated sleep apnea are significantly more likely to fall asleep at the wheel. Reaction times impaired by sleep deprivation are comparable to drunk driving — making this one of the most dangerous consequences of untreated OSA.',
    fact: '2.5× higher risk of vehicle accidents',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <rect x="22" y="36" width="36" height="20" rx="5" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M26 36 L30 26 Q32 22 38 22 L42 22 Q48 22 50 26 L54 36" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <circle cx="29" cy="56" r="4" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="51" cy="56" r="4" stroke="#c05675" strokeWidth="2" fill="none"/>
        <circle cx="40" cy="42" r="7" stroke="#c05675" strokeWidth="1.5" fill="none"/>
        <path d="M36 41 Q37 43 38 41" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <path d="M42 41 Q43 43 44 41" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
      </svg>
    ),
  },
  {
    title: 'Early Fatigue',
    description:
      'Even after a full night in bed, adults with sleep apnea wake feeling unrefreshed. The body expends enormous energy fighting to breathe during sleep, leaving muscles fatigued and energy reserves depleted from the first moments of the day.',
    fact: 'Fatigue persists regardless of sleep duration',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <rect x="24" y="42" width="32" height="14" rx="3" stroke="#c05675" strokeWidth="2" fill="none"/>
        <path d="M30 42 L30 36 Q30 26 40 26 Q50 26 50 36 L50 42" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <circle cx="40" cy="34" r="5" stroke="#c05675" strokeWidth="1.5" fill="none"/>
        <path d="M34 50 L36 50 M38 50 L46 50" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M55 24 L58 20 M57 24 L58 20 L55 20" stroke="#c05675" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M58 30 L62 30" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M56 27 L59 24" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: 'Uncontrolled Diabetes & Hypertension',
    description:
      'Sleep apnea triggers the release of stress hormones that raise blood sugar and blood pressure. Patients already managing diabetes or hypertension often find their conditions worsen significantly when sleep apnea goes untreated.',
    fact: 'OSA treatment improves blood pressure control',
    icon: (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <circle cx="40" cy="40" r="36" fill="#fbeaf0"/>
        <rect x="44" y="30" width="16" height="22" rx="2" stroke="#c05675" strokeWidth="1.8" fill="none"/>
        <rect x="46" y="34" width="12" height="6" rx="1" stroke="#c05675" strokeWidth="1.2" fill="none"/>
        <path d="M48 37 L50 37 L52 35 L54 39 L56 37 L58 37" stroke="#c05675" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <path d="M20 44 Q24 36 28 44 Q32 52 36 44" stroke="#c05675" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <circle cx="22" cy="44" r="2" fill="#c05675"/>
        <circle cx="34" cy="44" r="2" fill="#c05675"/>
        <path d="M20 50 L36 50" stroke="#c05675" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const AUTOPLAY_DELAY = 5000;

export default function AdultsSignsCarousel() {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);

  const goTo = (index) => {
    setCurrent((index + signs.length) % signs.length);
    setProgress(0);
    startTimeRef.current = Date.now();
  };

  const next = () => goTo(current + 1);
  const prev = () => goTo(current - 1);

  useEffect(() => {
    startTimeRef.current = Date.now();
    setProgress(0);

    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % signs.length);
      startTimeRef.current = Date.now();
      setProgress(0);
    }, AUTOPLAY_DELAY);

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress(Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100));
    }, 50);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [current]);

  const slide = signs[current];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-4xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
            Adult Sleep Apnea
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
            Common Signs & Symptoms in Adults
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Sleep apnea in adults often goes undiagnosed for years. Recognising these signs early can prevent serious long-term health consequences.
          </p>
        </div>

        {/* Thumbnail Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mb-10">
          {signs.map((sign, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`flex flex-col items-center gap-2 p-2 rounded-xl transition-all duration-200 group ${
                i === current
                  ? 'bg-primary/10 ring-2 ring-primary/40'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 overflow-hidden ${
                  i === current ? 'bg-primary/20' : 'bg-gray-100 group-hover:bg-primary/10'
                }`}
              >
                <div className="w-10 h-10">{sign.icon}</div>
              </div>
              <span
                className={`text-center leading-tight hidden sm:block transition-colors duration-200 ${
                  i === current
                    ? 'text-primary font-semibold text-xs'
                    : 'text-gray-500 text-xs group-hover:text-secondary'
                }`}
              >
                {sign.title}
              </span>
            </button>
          ))}
        </div>

        {/* Main Card */}
        <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">

          {/* Progress bar */}
          <div className="h-1 bg-gray-200">
            <div
              className="h-1 bg-primary"
              style={{ width: `${progress}%`, transition: 'width 0.05s linear' }}
            />
          </div>

          {/* Slide content */}
          <div className="flex flex-col md:flex-row items-center gap-8 p-8 md:p-12 min-h-[280px]">

            {/* Icon circle */}
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full bg-white border-4 border-primary/20 flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden">
              <div className="w-28 h-28 md:w-32 md:h-32">
                {slide.icon}
              </div>
            </div>

            {/* Text */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left gap-4 flex-1">

              <span className="text-xs font-semibold text-primary/60 tracking-widest uppercase">
                Sign {current + 1} of {signs.length}
              </span>

              <h3 className="text-2xl font-bold text-secondary">
                {slide.title}
              </h3>

              <p className="text-gray-600 text-lg leading-relaxed">
                {slide.description}
              </p>

              <div className="inline-block px-5 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                {slide.fact}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-white">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-secondary hover:border-secondary transition-colors"
              aria-label="Previous sign"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-center max-w-xs">
              {signs.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to sign ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === current
                      ? 'bg-primary w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-secondary hover:border-secondary transition-colors"
              aria-label="Next sign"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}